<script>
    /*
        PostalResultsPage.svelte

        This page now has "fixed" top and bottom sections.
        The bottom section contains left-side, middle and right-side sections.

    */

	import { onMount } from 'svelte';
    import { push, replace } from 'svelte-spa-router';
    import { fly } from 'svelte/transition';

	import { masterSearch } from '../../common/master-objects.js';
	import { runPublicPostalSearch, runPublicFilterSearch } from '../../common/public-search-utils.js';
    
    import { setErrorAlert, setWarningAlert } from '../../stores/AlertsStore';
    import { DEMOGRAPHICS, OPEN_ELIG_2022 } from "../../common/taxonomy-classes.js";
    import { validatePostalCode } from "../../common/utils.js";    
	import { isRunningSearch, isWindowNarrowView, isShowingZipError } from '../../stores/ScreenSettingsStore.js';
    import { AccountStore } from "../../common/account.ts";

    // TCARE components
    import PageAlert from '../../components/common/PageAlert.svelte';
    import PublicSearchPanel from '../../components/searches/PublicSearchPanel.svelte';
    // import CircularSpinner from '../../components/common/CircularSpinner.svelte';

    import VerticalResourceMenu from '../../components/resource-menu/VerticalResourceMenu.svelte';
    import Header from '../../components/header/Header.svelte';
    
    // Vito code
    import { DEMO_POSTAL_WARNING_MSG, isPostalLockedWithWrongPostal } from "../../../demo.js";
    // import { ifPostalLockedIsCorrectPostal } from "../../../demo.js";

    const SELECT_CATEGORY_MESSAGE = 'Select from these categories to show services in';
    const RUNNING_SEARCH_MESSAGE = 'Searching for services in';
    const NEW_SEARCH_MESSAGE = 'Please start a new search by entering a ZIP Code above';
    
    const POSTAL_WARNING_MSG = 'Please enter a valid ZIP Code.';
    const SEARCH_ERROR_MSG = 'An error occurred during your search.';

    const PATH_SVG = "img/butterfly_2.svg";

    $: staticPostalCode = '';       // prevent postal from changing when user types new one

    onMount( async () => {
        // console.log('');
        // console.log('PostalResultsPage onMount');
        // console.log('$masterSearch:');
        // console.log($masterSearch);

        if (!$AccountStore?.isLoggedIn) {
            replace('/landing');                                  // and login
            return;
        }

        staticPostalCode = $masterSearch.location.postalCode;       // when returning from another page
    })


    // returning true will force a FILTER search
    const searchHasFilters = () => {

        // first check the free text field
        if ($masterSearch.text && $masterSearch.text.trim() !== '') {
            return true;
        }

        // check for any keywords (or demographics) selected
        if ($masterSearch.getSelectedNodes().length > 0) {
            return true;
        }

        // all other conditions will force a new POSTAL search
        return false;
    }


    ///////////////////////////////   POSTAL, FILTER AND DEMOGRAPHIC SEARCH FUNCTIONS   ////////////////////////////////

    // this is the main ZIP Code search - no filters
    // fills the search.postalResults[] array
    // this search also redraws the ResourceMenu
    const runResourcePagePostalSearch = async () => {
        // console.log('ResourceExplorerPage runResourcePagePostalSearch: ' + $masterSearch.location.postalCode);

        if (!validatePostalCode($masterSearch.location.postalCode)) {
            $isShowingZipError = true
            return false;
        }
        if(isPostalLockedWithWrongPostal($masterSearch.location.postalCode)){
            setWarningAlert(DEMO_POSTAL_WARNING_MSG, 3000);
            return false;
        }

        staticPostalCode = $masterSearch.location.postalCode; 
        $masterSearch.postalResults = [];
        $masterSearch.clearTaxonomies();                                 // no filters when run from here
        $masterSearch.text = '';                                         // or null?
        $masterSearch = $masterSearch;

        $isRunningSearch = true;
        const results = await runPublicPostalSearch(masterSearch);       // in public-search-utils.js
        $isRunningSearch = false;
        $masterSearch = $masterSearch;

        if (results.errors) {
            $isShowingZipError = true
            $masterSearch.postalResults = [];                               // avoid error
            return false;
        } else {
            $isShowingZipError = false
            staticPostalCode = $masterSearch.location.postalCode;           // cache this for static UI display
            // console.log('postalResults:');
            // console.log($masterSearch.postalResults);
            return true;
        }
    }

    // this returns a different set of results - the postalResults are retained for the menu
    // fills the search.filterResults[] array
    // this search DOES NOT redraw the ResourceMenu
    // this search DOES redraw the filters checkboxes
    const runResourcePageFilterSearch = async () => {
        // console.log('ResourceExplorerPage runResourcePageFilterSearch: ' + $masterSearch.location.postalCode);

        if (!validatePostalCode($masterSearch.location.postalCode)) {
            $isShowingZipError = true
            return;
        }

        if (!searchHasFilters()) {
            runResourcePagePostalSearch();
            return;
        }

        $masterSearch.filterResults = [];

        $isRunningSearch = true;
        const results = await runPublicFilterSearch(masterSearch);       // in public-search-utils.js
        $isRunningSearch = false;

        if (results.errors) {
            $isShowingZipError = true
            $masterSearch.filterResults = [];                               // avoid null error downstream
            return false;
        } else {
            $isShowingZipError = false
            push('/explorer');
        }
    }


</script>

<style>

    .postal-results-page {
        position: relative;
        width: 100%;
        background-color: #391A52; 
        height: 100vh;
    }

    .postal-results-page .inner-page {
        z-index: 0;
        position: absolute;
        top: 50px;
        left: 0px;
        right: 0px;
        width: 96%;
        max-width: 1300px;
        margin: 0px auto;        
        /* border: 1px solid white; */
    }

    /* just beneath Header */
    .postal-results-page .fixed-top {
        position: relative;
        top: 0px;
        left: 0%;
        width: 100%;
        height: 120px;
        display: flex;
        align-items: center;
        justify-content: flex-start;
        /* border: 1px solid red; */
    }


    /* fixed-top elements */
    .postal-results-page .row-0 {
        width: 100%;
        /* border: 1px solid green; */
    }


    .svg-image {
        z-index: -1;
        position: fixed;
        bottom: -5px;
        left: 18%;
        width: 500px;
    }

    .svg-image img {
        width: 100%;
    }

    .message-row {
        display: flex;
        align-items: flex-start;
        justify-content: center;
    }

    .select-cat {
        width: 325px;
        margin-top: 20px;
        font-size: 36px;
        font-weight: 400;
        line-height: 44px;
        text-align: right;
        color: var(--tcui-secondary);
        /* border: 1px solid white; */
    }

    .select-cat span {
        color: var(--tcui-white);
    }

    .new-search {
        margin: auto;
        margin-top: 50px;
        font-size: 36px;
        font-weight: 400;
        line-height: 40px;
        text-align: center;
        color: var(--tcui-secondary);
    }

    .new-search span {
        color: var(--tcui-white);
    }


    .w-100 {
        width: 100%;
    }

    .vertical-menu-center {
        width: fit-content;
        padding: 30px 60px;
        margin-left: 50px;
        background-color: white;
        border-radius: 40px;
    }

    .mobile-top {
        padding: 10px 0px;
        margin-bottom: 10px;
        text-align: center;
        font-size: 16px;
        width: 100%;
        color: white;
        border-bottom: 1px solid purple;
    }


    @keyframes fade-in {
        from {opacity: 0}
        to {opacity: 1}
    }

    @keyframes fade-in-from-right {
        from {opacity: 0; left: 200px;}
        to {opacity: 1; left: 0px;}
    }

    

    @media screen and (max-width: 600px) {
    }

    @media screen and (min-width: 601px) and (max-width: 1300px) {
    }

    @media screen and (min-width: 1201px) {
    }

</style>





<div class="postal-results-page" >

    <Header user={$AccountStore} />
    <PageAlert />

    <div class="inner-page">
        <!-- MOBILE VIEW -->
        {#if $isWindowNarrowView}
            <div class="mobile-top">Mobile View</div>
            <div class="mobile-top">Mobile Explorer Page TBA</div>
        {/if}


        <!-- NOT MOBILE VIEW -->
        {#if !$isWindowNarrowView}
            <!-- always show the top search bar even when searching -->    
            <div class="fixed-top">
                <div class="flex-row row-0">
                    <PublicSearchPanel store={masterSearch} 
                        onPostalSearch={runResourcePagePostalSearch} 
                        onFilterSearch={runResourcePageFilterSearch}     
                        onDemographicsSearch={null}     
                        onShowFiltersClick={null}
                        isShowingFreeText={false}
                        isShowingZipError={isShowingZipError}
                    />
                </div>   
            </div>

            {#if $masterSearch.postalResults.length > 0}
                <div class="message-row" in:fly="{{ x: -100, duration: 250 }}" >
                    <div class="select-cat">{SELECT_CATEGORY_MESSAGE} <span>{staticPostalCode}</span></div>
                    <div class="vertical-menu-center">
                        <VerticalResourceMenu
                            searchStore={masterSearch} 
                            taxonomyName={OPEN_ELIG_2022} 
                            onSearch={runResourcePageFilterSearch}
                            isAnimated={false}
                        />
                    </div>
                </div>
            {:else}
                <!-- show the message alone with no menu -->
                <div class="new-search w-100">{NEW_SEARCH_MESSAGE}</div>
            {/if}

            <div class="svg-image">
                <img src={PATH_SVG} alt="SVG Shape" draggable="false"/>
            </div>
        {/if}
    </div>

</div>

